'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  ArrowRight,
  BookOpen,
  Check,
  Clapperboard,
  Download,
  FileText,
  Film,
  GraduationCap,
  Mic,
  Monitor,
  Pause,
  Play,
  Presentation,
  Search,
  Sparkles,
  Upload,
  Users,
  Video,
  Volume2
} from 'lucide-react';

// Related features data
const relatedFeatures = [
  {
    title: 'AI Product Video Maker',
    description:
      'Turn raw screen recordings into polished, publish-ready product videos with AI-powered editing and voiceovers.',
    badge: 'Product Videos',
    icon: Video,
    path: '/tools/ai-product-video-maker',
    ctaText: 'Start Creating',
    design: 'editor'
  },
  {
    title: 'Product Demo Video Maker',
    description:
      'Create professional product demos with automated zoom effects, annotations, and transitions.',
    badge: 'Demo Videos',
    icon: Clapperboard,
    path: '/tools/product-demo-video-maker',
    ctaText: 'Create Demo',
    design: 'demo'
  },
  {
    title: 'AI Video Script Generator',
    description:
      'Generate engaging, professional video scripts instantly from your screen recordings.',
    badge: 'Script Generator',
    icon: FileText,
    path: '/tools/ai-video-script-generator',
    ctaText: 'Generate Script',
    design: 'script'
  },
  {
    title: 'AI Tutorial Video Maker',
    description:
      'Transform screen recordings into polished tutorials with AI-generated voiceovers.',
    badge: 'Tutorial Maker',
    icon: Presentation,
    path: '/tools/ai-tutorial-video-maker',
    ctaText: 'Create Tutorial',
    design: 'tutorial'
  },
  {
    title: 'AI Educational Video Maker',
    description:
      'Build engaging educational content with interactive elements and professional narration.',
    badge: 'Education',
    icon: GraduationCap,
    path: '/tools/ai-educational-video-maker',
    ctaText: 'Start Teaching',
    design: 'education'
  },
  {
    title: 'AI Screen Recorder',
    description:
      'Record your screen in 4K resolution and turn recordings into narrated videos with AI.',
    badge: 'Screen Recorder',
    icon: Monitor,
    path: '/tools/ai-screen-recorder',
    ctaText: 'Start Recording',
    design: 'recorder'
  },
  {
    title: 'AI Training Videos',
    description:
      'Turn silent screen recordings into narrated training videos with AI scripts and voiceovers.',
    badge: 'Training Videos',
    icon: Video,
    path: '/tools/ai-training-videos',
    ctaText: 'Create Training Video',
    design: 'training'
  },
  {
    title: 'AI Voiceover Generator',
    description:
      'Upload a raw screen capture and get a professional AI voiceover, script and captions automatically.',
    badge: 'Voiceover',
    icon: Mic,
    path: '/tools/ai-voiceover-generator',
    ctaText: 'Generate Voiceover',
    design: 'voiceover'
  },
  {
    title: 'AI Avatar Video Maker',
    description:
      'Add an AI avatar presenter that delivers the intro, outro and transitions around your screen recording.',
    badge: 'AI Avatars',
    icon: Users,
    path: '/tools/ai-avatar-video-maker',
    ctaText: 'Add a Presenter',
    design: 'avatar'
  },
  {
    title: 'AI Video Clone',
    description:
      'Train an AI clone of yourself from 15 seconds of footage and present every video without filming again.',
    badge: 'Clone Yourself',
    icon: Users,
    path: '/tools/ai-video-clone',
    ctaText: 'Clone Yourself',
    design: 'clone'
  },
  {
    title: 'AI Video Translator',
    description:
      'Re-render your video in 70+ languages with a translated script and a native AI voiceover.',
    badge: 'Translation',
    icon: Volume2,
    path: '/tools/ai-video-translator',
    ctaText: 'Translate a Video',
    design: 'translate'
  },
  {
    title: 'AI Voice Cloning',
    description:
      'Connect your ElevenLabs account and narrate every video in your own cloned voice.',
    badge: 'Voice Cloning',
    icon: Mic,
    path: '/tools/ai-voice-cloning',
    ctaText: 'Use Your Voice',
    design: 'voiceclone'
  }
];

// ─── Shared building blocks ───

const MockupShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-card rounded-xl border border-border overflow-hidden h-full flex flex-col">
    <div className="border-b border-border px-3 py-1.5 flex items-center gap-1.5">
      <div className="w-[7px] h-[7px] rounded-full bg-muted-foreground/20" />
      <div className="w-[7px] h-[7px] rounded-full bg-muted-foreground/20" />
      <div className="w-[7px] h-[7px] rounded-full bg-muted-foreground/20" />
    </div>
    <div className="flex-1 flex flex-col">{children}</div>
  </div>
);

// Consistent mockup header bar
const MockupHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  status?: string;
}> = ({ icon, title, status }) => (
  <div className="px-3 py-1.5 border-b border-border flex items-center justify-between">
    <div className="flex items-center gap-1.5">
      {icon}
      <span className="text-[9px] font-semibold text-foreground">{title}</span>
    </div>
    {status && (
      <span className="text-[8px] text-primary font-medium">{status}</span>
    )}
  </div>
);

// ─── 1. AI Product Video Maker — Video editor with timeline + AI sidebar ───
const EditorUI = () => (
  <MockupShell>
    <MockupHeader
      icon={<Film className="w-3 h-3 text-primary" />}
      title="Video Editor"
      status="AI Enhanced"
    />
    {/* Main area: preview + AI panel */}
    <div className="flex flex-1 min-h-0">
      {/* Video preview */}
      <div className="flex-1 p-2 flex flex-col">
        <div className="bg-muted/50 rounded-md flex-1 flex items-center justify-center relative">
          <div className="w-6 h-6 rounded-full bg-primary/15 flex items-center justify-center">
            <Play className="w-3 h-3 text-primary ml-0.5" />
          </div>
          <span className="absolute bottom-1 right-1.5 text-[8px] text-muted-foreground font-mono">
            01:24
          </span>
        </div>
        {/* Timeline tracks */}
        <div className="mt-1.5 space-y-[3px]">
          <div className="flex items-center gap-1">
            <span className="text-[7px] text-muted-foreground w-6 shrink-0">
              Video
            </span>
            <div className="flex-1 h-2.5 bg-primary/20 rounded-sm relative">
              <div className="absolute left-[35%] top-0 bottom-0 w-[1px] bg-primary" />
            </div>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[7px] text-muted-foreground w-6 shrink-0">
              Voice
            </span>
            <div className="flex-1 h-2.5 bg-primary/12 rounded-sm flex items-center px-1 gap-[2px]">
              {[3, 5, 2, 6, 4, 7, 3, 5, 6, 2, 4, 7, 5, 3, 6, 4, 2, 5, 7, 3].map(
                (h, i) => (
                  <div
                    key={i}
                    className="w-[2px] bg-primary/40 rounded-full"
                    style={{ height: `${h}px` }}
                  />
                )
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[7px] text-muted-foreground w-6 shrink-0">
              Music
            </span>
            <div className="flex-1 h-2.5 bg-primary/8 rounded-sm" />
          </div>
        </div>
      </div>
      {/* AI sidebar */}
      <div className="w-[72px] border-l border-border p-1.5 flex flex-col gap-1.5 bg-muted/20">
        <div className="text-[8px] font-semibold text-foreground flex items-center gap-1">
          <Sparkles className="w-2.5 h-2.5 text-primary" />
          AI Tools
        </div>
        {['B-Rolls', 'Voiceover', 'Captions', 'Music'].map((item) => (
          <div
            key={item}
            className="bg-card border border-border rounded px-1.5 py-1 text-[8px] text-muted-foreground text-center hover:border-primary/30 transition-colors"
          >
            {item}
          </div>
        ))}
      </div>
    </div>
  </MockupShell>
);

// ─── 2. Product Demo — Annotated product walkthrough ───
const DemoUI = () => (
  <MockupShell>
    <MockupHeader
      icon={<Clapperboard className="w-3 h-3 text-primary" />}
      title="Demo Builder"
      status="Step 2/4"
    />
    <div className="flex-1 p-2 flex flex-col gap-1.5">
      {/* Screen capture preview with annotations */}
      <div className="bg-muted/40 rounded-md flex-1 relative overflow-hidden">
        {/* Simulated app UI being demoed */}
        <div className="absolute inset-1.5 bg-muted/60 rounded border border-border/50 p-1.5">
          {/* Fake app nav */}
          <div className="flex items-center gap-1 mb-1.5 pb-1 border-b border-border/30">
            <div className="w-2.5 h-2.5 rounded bg-primary/20" />
            <div className="w-8 h-1 bg-muted-foreground/15 rounded" />
            <div className="ml-auto flex gap-1">
              <div className="w-5 h-1 bg-muted-foreground/10 rounded" />
              <div className="w-5 h-1 bg-muted-foreground/10 rounded" />
            </div>
          </div>
          {/* Fake app content */}
          <div className="flex gap-1">
            <div className="w-[28%] space-y-[3px]">
              <div className="h-1.5 bg-muted-foreground/10 rounded" />
              <div className="h-1.5 bg-primary/20 rounded" />
              <div className="h-1.5 bg-muted-foreground/10 rounded w-3/4" />
            </div>
            <div className="flex-1 space-y-1">
              <div className="h-4 bg-muted-foreground/8 rounded" />
              <div className="flex gap-[3px]">
                <div className="h-3 flex-1 bg-muted-foreground/8 rounded" />
                <div className="h-3 flex-1 bg-muted-foreground/8 rounded" />
              </div>
            </div>
          </div>
        </div>
        {/* Annotation callout with pointer */}
        <div className="absolute top-2.5 right-2 bg-primary text-primary-foreground rounded px-1.5 py-0.5 text-[6px] font-bold shadow-md flex items-center gap-0.5">
          <span>Click "Settings"</span>
        </div>
        {/* Cursor with click ring */}
        <div className="absolute top-[55%] left-[48%]">
          <div className="w-3.5 h-3.5 rounded-full border-2 border-primary/50 animate-ping absolute -top-[3px] -left-[3px]" />
          <div className="w-2 h-2 rounded-full bg-primary shadow-sm" />
        </div>
        {/* Zoom badge */}
        <div className="absolute bottom-1 right-1 bg-primary/10 border border-primary/25 rounded px-1 py-0.5 flex items-center gap-0.5">
          <Search className="w-2 h-2 text-primary" />
          <span className="text-[6px] text-primary font-medium">2x</span>
        </div>
      </div>
      {/* Step progress bar */}
      <div className="bg-card rounded-md border border-border p-1.5">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[8px] font-semibold text-foreground">
            Demo Progress
          </span>
          <span className="text-[7px] text-primary font-medium">2 / 4</span>
        </div>
        <div className="flex gap-1">
          {[true, true, false, false].map((done, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full ${done ? 'bg-primary' : 'bg-muted'}`}
            />
          ))}
        </div>
      </div>
      {/* Effects toolbar */}
      <div className="grid grid-cols-3 gap-1">
        {[
          { label: 'Auto-Zoom', active: true },
          { label: 'Annotations', active: true },
          { label: 'Captions', active: false }
        ].map((fx) => (
          <div
            key={fx.label}
            className={`text-[8px] text-center py-1 rounded border ${fx.active ? 'bg-primary text-primary-foreground font-semibold border-primary' : 'bg-card text-muted-foreground border-border'}`}
          >
            {fx.label}
          </div>
        ))}
      </div>
    </div>
  </MockupShell>
);

// ─── 3. AI Script Generator — Script writing interface ───
const ScriptUI = () => (
  <MockupShell>
    <MockupHeader
      icon={<Sparkles className="w-3 h-3 text-primary" />}
      title="Script Generator"
      status="Scene 3/5"
    />
    <div className="flex-1 p-2 flex flex-col gap-1.5">
      {/* Script content */}
      <div className="bg-card rounded-md border border-border p-2 flex-1 space-y-2">
        <div className="flex items-start gap-1.5">
          <div className="w-3.5 h-3.5 rounded bg-primary/15 flex items-center justify-center shrink-0 mt-0.5">
            <span className="text-[7px] text-primary font-bold">1</span>
          </div>
          <div>
            <p className="text-[9px] text-foreground leading-relaxed font-medium">
              "Welcome to our dashboard — here's everything you need to get
              started."
            </p>
            <span className="text-[7px] text-muted-foreground mt-0.5 block">
              0:00 – 0:04
            </span>
          </div>
        </div>
        <div className="flex items-start gap-1.5">
          <div className="w-3.5 h-3.5 rounded bg-primary/15 flex items-center justify-center shrink-0 mt-0.5">
            <span className="text-[7px] text-primary font-bold">2</span>
          </div>
          <div>
            <p className="text-[9px] text-foreground leading-relaxed font-medium">
              "Navigate to settings to configure your workspace preferences."
            </p>
            <span className="text-[7px] text-muted-foreground mt-0.5 block">
              0:04 – 0:08
            </span>
          </div>
        </div>
        <div className="flex items-start gap-1.5 opacity-50">
          <div className="w-3.5 h-3.5 rounded bg-muted flex items-center justify-center shrink-0 mt-0.5">
            <span className="text-[7px] text-muted-foreground font-bold">
              3
            </span>
          </div>
          <div>
            <p className="text-[9px] text-muted-foreground leading-relaxed">
              Generating<span className="animate-pulse">...</span>
            </p>
          </div>
        </div>
      </div>
      {/* Bottom bar */}
      <div className="flex gap-1.5">
        <div className="flex-1 bg-primary text-primary-foreground rounded-md py-1 flex items-center justify-center gap-1">
          <Sparkles className="w-2.5 h-2.5" />
          <span className="text-[8px] font-semibold">Regenerate</span>
        </div>
        <div className="flex-1 bg-card border border-border rounded-md py-1 flex items-center justify-center gap-1">
          <FileText className="w-2.5 h-2.5 text-primary" />
          <span className="text-[8px] font-semibold text-foreground">Edit</span>
        </div>
      </div>
    </div>
  </MockupShell>
);

// ─── 4. Tutorial Maker — Step-by-step tutorial builder ───
const TutorialUI = () => (
  <MockupShell>
    <MockupHeader
      icon={<Presentation className="w-3 h-3 text-primary" />}
      title="Tutorial Builder"
      status="Processing"
    />
    <div className="flex-1 p-2 flex flex-col gap-1.5">
      {/* Video preview with chapter markers */}
      <div className="bg-muted/40 rounded-md h-[42px] relative flex items-center justify-center">
        <div className="w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center">
          <Play className="w-2.5 h-2.5 text-primary ml-0.5" />
        </div>
        <span className="absolute bottom-1 right-1.5 text-[7px] text-muted-foreground font-mono">
          04:32
        </span>
        {/* Progress with chapter markers */}
        <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-muted-foreground/10">
          <div className="h-full w-[45%] bg-primary/50 rounded-r" />
          <div className="absolute top-[-1px] left-[25%] w-[3px] h-[5px] bg-primary rounded-full" />
          <div className="absolute top-[-1px] left-[55%] w-[3px] h-[5px] bg-primary/40 rounded-full" />
          <div className="absolute top-[-1px] left-[80%] w-[3px] h-[5px] bg-primary/40 rounded-full" />
        </div>
      </div>
      {/* Chapters */}
      <div className="bg-card rounded-md border border-border p-1.5 flex-1">
        <span className="text-[8px] font-semibold text-foreground mb-1 block">
          Chapters
        </span>
        {[
          { title: 'Getting Started', time: '0:00', done: true },
          { title: 'Creating Your First Project', time: '1:12', done: true },
          { title: 'Advanced Settings', time: '2:45', done: false }
        ].map((ch, i) => (
          <div
            key={i}
            className="flex items-center justify-between py-[3px]"
          >
            <div className="flex items-center gap-1.5">
              <div
                className={`w-3 h-3 rounded flex items-center justify-center ${ch.done ? 'bg-primary' : 'bg-muted'}`}
              >
                {ch.done ? (
                  <Check className="w-2 h-2 text-primary-foreground" />
                ) : (
                  <span className="text-[7px] text-muted-foreground">
                    {i + 1}
                  </span>
                )}
              </div>
              <span
                className={`text-[9px] ${ch.done ? 'text-foreground font-medium' : 'text-muted-foreground'}`}
              >
                {ch.title}
              </span>
            </div>
            <span className="text-[7px] text-muted-foreground font-mono">
              {ch.time}
            </span>
          </div>
        ))}
      </div>
      {/* Voice selection */}
      <div className="flex items-center gap-1.5 bg-card rounded-md border border-border p-1.5">
        <Mic className="w-3 h-3 text-primary" />
        <span className="text-[8px] text-foreground font-medium flex-1">
          Sarah — Natural
        </span>
        <Volume2 className="w-2.5 h-2.5 text-muted-foreground" />
      </div>
    </div>
  </MockupShell>
);

// ─── 5. Education — Course builder with lessons ───
const EducationUI = () => (
  <MockupShell>
    <MockupHeader
      icon={<GraduationCap className="w-3 h-3 text-primary" />}
      title="Course Builder"
      status="Module 2"
    />
    <div className="flex-1 p-2 flex flex-col gap-1.5">
      {/* Course progress */}
      <div className="bg-card rounded-md border border-border p-1.5">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[8px] font-semibold text-foreground">
            Course Progress
          </span>
          <span className="text-[8px] text-primary font-bold">62%</span>
        </div>
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full"
            style={{ width: '62%' }}
          />
        </div>
      </div>
      {/* Lessons */}
      <div className="bg-card rounded-md border border-border p-1.5 flex-1">
        <span className="text-[8px] font-semibold text-foreground mb-1 block">
          Lessons
        </span>
        {[
          { title: 'Introduction to AI', status: 'done' },
          { title: 'Machine Learning Basics', status: 'done' },
          { title: 'Neural Networks', status: 'current' },
          { title: 'Practical Applications', status: 'locked' }
        ].map((lesson, i) => (
          <div
            key={i}
            className={`flex items-center gap-1.5 py-[3px] px-1 rounded ${lesson.status === 'current' ? 'bg-primary/8' : ''}`}
          >
            <div
              className={`w-3 h-3 rounded-full flex items-center justify-center shrink-0 ${
                lesson.status === 'done'
                  ? 'bg-primary'
                  : lesson.status === 'current'
                    ? 'bg-primary/20 border border-primary'
                    : 'bg-muted'
              }`}
            >
              {lesson.status === 'done' ? (
                <Check className="w-2 h-2 text-primary-foreground" />
              ) : lesson.status === 'current' ? (
                <Play className="w-1.5 h-1.5 text-primary ml-[1px]" />
              ) : (
                <span className="text-[7px] text-muted-foreground">
                  {i + 1}
                </span>
              )}
            </div>
            <span
              className={`text-[9px] ${lesson.status === 'locked' ? 'text-muted-foreground/50' : lesson.status === 'current' ? 'text-primary font-medium' : 'text-foreground'}`}
            >
              {lesson.title}
            </span>
          </div>
        ))}
      </div>
      {/* Quiz indicator */}
      <div className="flex items-center gap-1.5 bg-primary/8 rounded-md border border-primary/20 p-1.5">
        <BookOpen className="w-3 h-3 text-primary" />
        <span className="text-[8px] text-foreground font-medium flex-1">
          Quiz: 5 Questions
        </span>
        <span className="text-[7px] text-primary font-semibold">Start →</span>
      </div>
    </div>
  </MockupShell>
);

// ─── 6. Screen Recorder — Recording interface ───
const RecorderUI = () => (
  <MockupShell>
    <MockupHeader
      icon={<Monitor className="w-3 h-3 text-primary" />}
      title="Screen Recorder"
      status="4K Ready"
    />
    <div className="flex-1 p-2 flex flex-col gap-1.5">
      {/* Screen capture preview */}
      <div className="bg-muted/40 rounded-md flex-1 relative overflow-hidden">
        {/* Simulated captured screen */}
        <div className="absolute inset-1.5 bg-muted/60 rounded border border-border/50 p-1.5">
          <div className="flex gap-1 mb-1">
            <div className="w-5 h-1 bg-muted-foreground/15 rounded" />
            <div className="w-4 h-1 bg-muted-foreground/15 rounded" />
            <div className="w-6 h-1 bg-muted-foreground/15 rounded" />
          </div>
          <div className="flex gap-1">
            <div className="w-1/3 h-8 bg-muted-foreground/10 rounded" />
            <div className="flex-1 h-8 bg-muted-foreground/10 rounded" />
          </div>
        </div>
        {/* REC indicator */}
        <div className="absolute top-1 left-1 bg-destructive text-destructive-foreground text-[7px] px-1.5 py-[2px] rounded-full flex items-center gap-1 font-semibold">
          <div className="w-1.5 h-1.5 bg-destructive-foreground rounded-full animate-pulse" />
          REC
        </div>
        {/* Timer */}
        <div className="absolute top-1 right-1 bg-background/80 text-foreground text-[8px] px-1.5 py-[2px] rounded font-mono">
          02:34
        </div>
        {/* Webcam bubble */}
        <div className="absolute bottom-1.5 right-1.5 w-7 h-7 rounded-full bg-primary/15 border-2 border-primary/40 flex items-center justify-center">
          <Users className="w-3 h-3 text-primary/60" />
        </div>
      </div>
      {/* Recording options */}
      <div className="bg-card rounded-md border border-border p-1.5">
        <div className="grid grid-cols-3 gap-1">
          {[
            { label: 'Screen', active: true },
            { label: 'Window', active: false },
            { label: 'Tab', active: false }
          ].map((opt) => (
            <div
              key={opt.label}
              className={`text-[8px] text-center py-1 rounded ${opt.active ? 'bg-primary text-primary-foreground font-semibold' : 'bg-muted/50 text-muted-foreground'}`}
            >
              {opt.label}
            </div>
          ))}
        </div>
      </div>
      {/* Controls */}
      <div className="flex items-center justify-center gap-2 py-0.5">
        <div className="flex items-center gap-1">
          <Mic className="w-3 h-3 text-primary" />
          <span className="text-[7px] text-muted-foreground">ON</span>
        </div>
        <div className="w-7 h-7 rounded-full bg-destructive flex items-center justify-center">
          <Pause className="w-3 h-3 text-destructive-foreground" />
        </div>
        <div className="flex items-center gap-1">
          <Monitor className="w-3 h-3 text-primary" />
          <span className="text-[7px] text-muted-foreground">4K</span>
        </div>
      </div>
    </div>
  </MockupShell>
);

// ─── 7. Training Videos — Record → Script → Voice pipeline ───
const TrainingUI = () => (
  <MockupShell>
    <MockupHeader
      icon={<Video className="w-3 h-3 text-primary" />}
      title="Training Video"
      status="Ready"
    />
    <div className="flex-1 p-2 flex flex-col gap-1.5">
      {/* Pipeline steps */}
      <div className="bg-card rounded-md border border-border p-1.5">
        <span className="text-[8px] font-semibold text-foreground mb-1 block">
          Production Pipeline
        </span>
        {[
          { icon: Upload, label: 'Screen Recording', status: 'done' },
          { icon: FileText, label: 'AI Script Generation', status: 'done' },
          { icon: Mic, label: 'AI Voiceover', status: 'done' },
          { icon: Download, label: 'Export 4K Video', status: 'current' }
        ].map((step, i) => (
          <div
            key={i}
            className="flex items-center gap-1.5 py-[3px]"
          >
            <div
              className={`w-3.5 h-3.5 rounded flex items-center justify-center shrink-0 ${
                step.status === 'done'
                  ? 'bg-primary'
                  : 'bg-primary/20 border border-primary'
              }`}
            >
              {step.status === 'done' ? (
                <Check className="w-2 h-2 text-primary-foreground" />
              ) : (
                <step.icon className="w-2 h-2 text-primary" />
              )}
            </div>
            <span
              className={`text-[9px] flex-1 ${step.status === 'current' ? 'text-primary font-medium' : 'text-foreground'}`}
            >
              {step.label}
            </span>
            {step.status === 'done' && (
              <span className="text-[7px] text-primary">✓</span>
            )}
          </div>
        ))}
      </div>
      {/* Audio waveform preview */}
      <div className="bg-card rounded-md border border-border p-1.5">
        <div className="flex items-center gap-1.5 mb-1">
          <Volume2 className="w-2.5 h-2.5 text-primary" />
          <span className="text-[8px] font-semibold text-foreground">
            AI Voiceover Preview
          </span>
        </div>
        <div className="flex items-center gap-[2px] h-4">
          {[
            2, 4, 6, 3, 7, 5, 8, 4, 6, 3, 5, 7, 4, 6, 8, 3, 5, 7, 4, 6, 3, 5, 7,
            8, 4
          ].map((h, i) => (
            <div
              key={i}
              className="w-[3px] bg-primary/30 rounded-full"
              style={{ height: `${h * 1.8}px` }}
            />
          ))}
        </div>
      </div>
      {/* Export button */}
      <div className="bg-primary text-primary-foreground rounded-md py-1.5 flex items-center justify-center gap-1 mt-auto">
        <Download className="w-3 h-3" />
        <span className="text-[9px] font-semibold">Export Training Video</span>
      </div>
    </div>
  </MockupShell>
);

// ─── 8. Voiceover — Voice picker + waveform ───
const VoiceoverUI = () => (
  <MockupShell>
    <MockupHeader
      icon={<Mic className="w-3 h-3 text-primary" />}
      title="AI Voiceover"
      status="Synced"
    />
    <div className="flex-1 p-2 flex flex-col gap-1.5">
      <div className="bg-card rounded-md border border-border p-1.5 flex-1">
        <span className="text-[8px] font-semibold text-foreground mb-1 block">
          Voices
        </span>
        {[
          { name: 'Sarah — Natural', active: true },
          { name: 'James — Confident', active: false },
          { name: 'Priya — Warm', active: false }
        ].map((voice) => (
          <div
            key={voice.name}
            className={`flex items-center gap-1.5 py-[3px] px-1 rounded ${voice.active ? 'bg-primary/8' : ''}`}
          >
            <div
              className={`w-3 h-3 rounded-full flex items-center justify-center shrink-0 ${voice.active ? 'bg-primary' : 'bg-muted'}`}
            >
              {voice.active ? (
                <Play className="w-1.5 h-1.5 text-primary-foreground ml-[1px]" />
              ) : (
                <Mic className="w-2 h-2 text-muted-foreground" />
              )}
            </div>
            <span
              className={`text-[9px] ${voice.active ? 'text-primary font-medium' : 'text-foreground'}`}
            >
              {voice.name}
            </span>
          </div>
        ))}
      </div>
      <div className="bg-card rounded-md border border-border p-1.5">
        <div className="flex items-center gap-1.5 mb-1">
          <Volume2 className="w-2.5 h-2.5 text-primary" />
          <span className="text-[8px] font-semibold text-foreground">
            Narration Preview
          </span>
        </div>
        <div className="flex items-center gap-[2px] h-4">
          {[3, 6, 4, 7, 5, 8, 4, 6, 3, 7, 5, 8, 6, 4, 7, 3, 5, 8, 6, 4, 7, 5].map(
            (h, i) => (
              <div
                key={i}
                className="w-[3px] bg-primary/30 rounded-full"
                style={{ height: `${h * 1.8}px` }}
              />
            )
          )}
        </div>
      </div>
    </div>
  </MockupShell>
);

// ─── 9. Avatar — Presenter placement around the recording ───
const AvatarUI = () => (
  <MockupShell>
    <MockupHeader
      icon={<Users className="w-3 h-3 text-primary" />}
      title="Avatar Presenter"
      status="1080p"
    />
    <div className="flex-1 p-2 flex flex-col gap-1.5">
      <div className="bg-muted/40 rounded-md flex-1 relative overflow-hidden flex items-center justify-center">
        <div className="w-9 h-9 rounded-full bg-primary/15 border-2 border-primary/40 flex items-center justify-center">
          <Users className="w-4 h-4 text-primary/70" />
        </div>
        <span className="absolute bottom-1 left-1.5 text-[7px] text-muted-foreground">
          Intro clip
        </span>
        <span className="absolute top-1 right-1 bg-primary/10 border border-primary/25 rounded px-1 py-0.5 text-[6px] text-primary font-medium">
          Standard
        </span>
      </div>
      <div className="bg-card rounded-md border border-border p-1.5">
        <span className="text-[8px] font-semibold text-foreground mb-1 block">
          Placement
        </span>
        <div className="grid grid-cols-3 gap-1">
          {[
            { label: 'Intro', active: true },
            { label: 'Transitions', active: true },
            { label: 'Outro', active: false }
          ].map((slot) => (
            <div
              key={slot.label}
              className={`text-[8px] text-center py-1 rounded border ${slot.active ? 'bg-primary text-primary-foreground font-semibold border-primary' : 'bg-card text-muted-foreground border-border'}`}
            >
              {slot.label}
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <span className="text-[7px] text-muted-foreground w-8 shrink-0">
          Timeline
        </span>
        <div className="flex-1 h-2.5 rounded-sm flex gap-[2px] overflow-hidden">
          <div className="w-[12%] bg-primary rounded-sm" />
          <div className="flex-1 bg-primary/15 rounded-sm" />
          <div className="w-[8%] bg-primary rounded-sm" />
          <div className="flex-1 bg-primary/15 rounded-sm" />
        </div>
      </div>
    </div>
  </MockupShell>
);

// ─── 10. Clone — Footage upload → consent → ready ───
const CloneUI = () => (
  <MockupShell>
    <MockupHeader
      icon={<Users className="w-3 h-3 text-primary" />}
      title="Train Your Clone"
      status="Ready"
    />
    <div className="flex-1 p-2 flex flex-col gap-1.5">
      <div className="bg-card rounded-md border border-border p-1.5 flex-1">
        {[
          { icon: Upload, label: 'Upload 15s of footage', status: 'done' },
          { icon: Check, label: 'Record consent', status: 'done' },
          { icon: Sparkles, label: 'Training avatar', status: 'done' },
          { icon: Play, label: 'Present your videos', status: 'current' }
        ].map((step, i) => (
          <div
            key={i}
            className="flex items-center gap-1.5 py-[3px]"
          >
            <div
              className={`w-3.5 h-3.5 rounded flex items-center justify-center shrink-0 ${step.status === 'done' ? 'bg-primary' : 'bg-primary/20 border border-primary'}`}
            >
              {step.status === 'done' ? (
                <Check className="w-2 h-2 text-primary-foreground" />
              ) : (
                <step.icon className="w-2 h-2 text-primary" />
              )}
            </div>
            <span
              className={`text-[9px] flex-1 ${step.status === 'current' ? 'text-primary font-medium' : 'text-foreground'}`}
            >
              {step.label}
            </span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-1.5 bg-primary/8 rounded-md border border-primary/20 p-1.5">
        <div className="w-5 h-5 rounded-full bg-primary/15 border border-primary/40 flex items-center justify-center shrink-0">
          <Users className="w-2.5 h-2.5 text-primary/70" />
        </div>
        <span className="text-[8px] text-foreground font-medium flex-1">
          Your Avatar — 3 looks
        </span>
        <span className="text-[7px] text-primary font-semibold">READY</span>
      </div>
    </div>
  </MockupShell>
);

// ─── 11. Translate — Language variants list ───
const TranslateUI = () => (
  <MockupShell>
    <MockupHeader
      icon={<Volume2 className="w-3 h-3 text-primary" />}
      title="Translations"
      status="70+ languages"
    />
    <div className="flex-1 p-2 flex flex-col gap-1.5">
      <div className="bg-card rounded-md border border-border p-1.5 flex-1">
        <span className="text-[8px] font-semibold text-foreground mb-1 block">
          Language Versions
        </span>
        {[
          { lang: 'English', note: 'Original', done: true },
          { lang: 'Spanish', note: 'Native voiceover', done: true },
          { lang: 'German', note: 'Rendering…', done: false }
        ].map((row) => (
          <div
            key={row.lang}
            className="flex items-center justify-between py-[3px]"
          >
            <div className="flex items-center gap-1.5">
              <div
                className={`w-3 h-3 rounded-full flex items-center justify-center shrink-0 ${row.done ? 'bg-primary' : 'bg-primary/20 border border-primary'}`}
              >
                {row.done ? (
                  <Check className="w-2 h-2 text-primary-foreground" />
                ) : (
                  <Play className="w-1.5 h-1.5 text-primary ml-[1px]" />
                )}
              </div>
              <span className="text-[9px] text-foreground font-medium">
                {row.lang}
              </span>
            </div>
            <span className="text-[7px] text-muted-foreground">{row.note}</span>
          </div>
        ))}
      </div>
      <div className="bg-card rounded-md border border-border p-1.5">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[8px] font-semibold text-foreground">
            Script → Voiceover → Render
          </span>
          <span className="text-[7px] text-primary font-medium">
            Per language
          </span>
        </div>
        <div className="flex gap-1">
          {[true, true, false].map((done, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full ${done ? 'bg-primary' : 'bg-muted'}`}
            />
          ))}
        </div>
      </div>
    </div>
  </MockupShell>
);

// ─── 12. Voice clone — BYOK import ───
const VoiceCloneUI = () => (
  <MockupShell>
    <MockupHeader
      icon={<Mic className="w-3 h-3 text-primary" />}
      title="Voice Library"
      status="Connected"
    />
    <div className="flex-1 p-2 flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5 bg-primary/8 rounded-md border border-primary/20 p-1.5">
        <Check className="w-3 h-3 text-primary" />
        <span className="text-[8px] text-foreground font-medium flex-1">
          ElevenLabs account connected
        </span>
      </div>
      <div className="bg-card rounded-md border border-border p-1.5 flex-1">
        <span className="text-[8px] font-semibold text-foreground mb-1 block">
          Your Voices
        </span>
        {[
          { name: 'My Voice', tag: 'Cloned', ready: true },
          { name: 'Founder Update', tag: 'Cloned', ready: true },
          { name: 'Sarah — Natural', tag: 'Stock', ready: true }
        ].map((voice) => (
          <div
            key={voice.name}
            className="flex items-center justify-between py-[3px]"
          >
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                <Mic className="w-2 h-2 text-primary" />
              </div>
              <span className="text-[9px] text-foreground font-medium">
                {voice.name}
              </span>
            </div>
            <span className="text-[7px] text-primary font-medium">
              {voice.tag}
            </span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-1.5 bg-card rounded-md border border-border p-1.5">
        <Volume2 className="w-3 h-3 text-primary" />
        <span className="text-[8px] text-foreground font-medium flex-1">
          Narrating in your voice
        </span>
        <Play className="w-2.5 h-2.5 text-primary" />
      </div>
    </div>
  </MockupShell>
);

const getUIComponent = (design: string) => {
  switch (design) {
    case 'editor':
      return <EditorUI />;
    case 'recorder':
      return <RecorderUI />;
    case 'script':
      return <ScriptUI />;
    case 'tutorial':
      return <TutorialUI />;
    case 'education':
      return <EducationUI />;
    case 'demo':
      return <DemoUI />;
    case 'training':
      return <TrainingUI />;
    case 'voiceover':
      return <VoiceoverUI />;
    case 'avatar':
      return <AvatarUI />;
    case 'clone':
      return <CloneUI />;
    case 'translate':
      return <TranslateUI />;
    case 'voiceclone':
      return <VoiceCloneUI />;
    default:
      return <EditorUI />;
  }
};

// Feature Card Component
const FeatureCard: React.FC<{ feature: (typeof relatedFeatures)[0] }> = ({
  feature
}) => (
  <Link
    href={feature.path}
    className="flex-shrink-0 w-[280px] sm:w-[320px] snap-start group cursor-pointer"
  >
    <div className="bg-card rounded-2xl overflow-hidden border border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-xl hover:shadow-primary/5 hover:-translate-y-1 h-full flex flex-col">
      {/* Mockup */}
      <div className="relative h-52 sm:h-56 p-3 bg-muted/20">
        {getUIComponent(feature.design)}
      </div>

      {/* Content */}
      <div className="p-4 sm:p-5 flex flex-col flex-1">
        <h3 className="font-inter text-base sm:text-lg font-bold text-foreground mb-1.5 group-hover:text-primary transition-colors line-clamp-1">
          {feature.title}
        </h3>
        <p className="text-muted-foreground text-sm leading-relaxed mb-5 line-clamp-2 flex-1">
          {feature.description}
        </p>
        <span className="inline-flex items-center gap-2 text-primary font-semibold text-xs uppercase tracking-wider group-hover:gap-2.5 transition-all duration-300">
          {feature.ctaText}
          <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
        </span>
      </div>
    </div>
  </Link>
);

const ExploreMoreSection: React.FC = () => {
  const currentPath = usePathname();
  const otherFeatures = relatedFeatures.filter((f) => f.path !== currentPath);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  const scrollLeft = () => {
    scrollContainerRef.current?.scrollBy({ left: -360, behavior: 'smooth' });
  };

  const scrollRight = () => {
    scrollContainerRef.current?.scrollBy({ left: 360, behavior: 'smooth' });
  };

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-[#FDFFF7] dark:bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 sm:mb-12">
          <h2 className="font-inter text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground">
            Explore more tools
          </h2>
          <div className="flex items-center gap-3">
            <button
              onClick={scrollLeft}
              className="w-11 h-11 rounded-xl bg-card border border-border hover:border-primary hover:bg-primary/5 flex items-center justify-center transition-all duration-300 shadow-sm"
              aria-label="Scroll left"
            >
              <ArrowRight className="w-5 h-5 text-foreground rotate-180" />
            </button>
            <button
              onClick={scrollRight}
              className="w-11 h-11 rounded-xl bg-primary hover:bg-primary/90 flex items-center justify-center transition-all duration-300 shadow-lg shadow-primary/20"
              aria-label="Scroll right"
            >
              <ArrowRight className="w-5 h-5 text-primary-foreground" />
            </button>
          </div>
        </div>

        {/* Carousel */}
        <div className="relative -mx-4 sm:-mx-6 px-4 sm:px-6">
          <div
            ref={scrollContainerRef}
            className="flex gap-5 sm:gap-6 overflow-x-auto pt-2 pb-4 snap-x snap-mandatory scroll-smooth scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {otherFeatures.map((feature) => (
              <FeatureCard
                key={feature.path}
                feature={feature}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExploreMoreSection;
